import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar.jsx';
import InfoBadge from '../components/InfoBadge.jsx';
import AliAssistPanel from '../components/AliAssistPanel.jsx';
import api from '../lib/api.js';

const STEPS = ['Onboard', 'Upload', 'Map columns', 'Choose outputs', 'Run + deliver'];

const FORMATS = [
  { key: 'geojson', label: 'GeoJSON', desc: 'Web + JS-friendly point data',
    info: 'A modern, lightweight JSON format that every web map (Leaflet, Mapbox, OpenLayers) and most GIS tools can ingest directly. Best pick if your downstream consumer is a web app or a script.' },
  { key: 'kml', label: 'KML', desc: 'Google Earth / Maps compatible',
    info: 'XML format used by Google Earth and Google Maps. Convenient for sharing a map with a stakeholder who only uses Google tools.' },
  { key: 'csv', label: 'CSV', desc: 'Spreadsheet-ready, includes lat/lng',
    info: 'Plain comma-separated table with latitude and longitude columns plus your original attributes. Open in Excel or import into Power BI / Tableau.' },
  { key: 'shapefile', label: 'Shapefile (ZIP)', desc: 'ArcGIS, QGIS, the industry classic',
    info: 'The legacy GIS format: a ZIP containing .shp + .shx + .dbf + .prj. Every GIS desktop supports it. Pick this if a city engineer or planner is the audience.' },
  { key: 'fgdb', label: 'File Geodatabase', desc: 'ArcGIS Pro native',
    info: 'ArcGIS Pro’s native format. Choose if your client works entirely inside Esri tooling.' },
];

const BASEMAPS = [
  { key: 'World_Topo_Map',         label: 'Topographic', desc: 'Roads + hills + place names. Best all-purpose pick.' },
  { key: 'World_Street_Map',       label: 'Streets',     desc: 'Just roads. Cleanest read at a glance.' },
  { key: 'World_Imagery',          label: 'Satellite',   desc: 'Real photos from space. Best for showing buildings + terrain.' },
  { key: 'Canvas/World_Light_Gray_Base', label: 'Light gray', desc: 'Almost-blank background so your dots pop.' },
  { key: 'Canvas/World_Dark_Gray_Base',  label: 'Dark gray',  desc: 'Like Light gray, but black. Good for projector / TV.' },
];

const REPORT_SECTIONS = [
  { key: 'cover', label: 'Cover page', default: true, locked: true },
  { key: 'exec', label: 'Executive summary (250 words, auto-generated)' },
  { key: 'methodology', label: 'Methodology: geocoder, CRS, QA rules', default: true },
  { key: 'dictionary', label: 'Data dictionary (columns → type → sample)' },
  { key: 'quality', label: 'Quality assessment (QA report card)', default: true },
  { key: 'stats', label: 'Statistical summary (mean/median/quartiles)' },
  { key: 'maps', label: 'Map exhibits (3 images)', default: true },
  { key: 'hotspots', label: 'Top-N hotspot list' },
  { key: 'jurisdiction', label: 'Per-jurisdiction breakdown' },
  { key: 'rawdata', label: 'Raw data appendix (first 100 rows)' },
  { key: 'references', label: 'References + citations', default: true },
  { key: 'disclosure', label: 'Disclosure + license footer', default: true, locked: true },
];

const LOCATION_KINDS = [
  { key: 'latlng', label: 'I have GPS numbers (lat + lng)',
    info: 'Pick this if your spreadsheet already has GPS-style numbers like 37.5102 and -76.9890. This is the fastest and most accurate option because we skip the address lookup.' },
  { key: 'address', label: 'I have one address column (e.g. "100 Main St, Anytown, VA")',
    info: 'Pick this when every row has a single column with the full street + city + state in it. MAPTRIUM looks up the GPS coordinates for you (uses MAPTRIUM credits from your account).' },
  { key: 'street_city_state', label: 'I have street + city + state in SEPARATE columns',
    info: 'Pick this when the address is split across columns (one for street, one for city, one for state, optionally one for ZIP). MAPTRIUM glues them together and looks them up (uses MAPTRIUM credits from your account).' },
  { key: 'zip', label: 'I only have a ZIP code (least accurate)',
    info: 'Use only when ZIP is the most precise location you have. Each row ends up at the center of its ZIP code, which can be a mile or more from the real address.' },
];

const REPORT_SECTION_INFO = {
  cover: 'The first page: Atlas Geo Technologies brand block, your client name and project type, prepared-for and prepared-by, and the job ID. Always included.',
  exec: 'A short paragraph summarizing the run: row count, geocode hit rate, QA verdict, and delivery target. Sets the context before the technical detail.',
  methodology: 'Explains exactly what MAPTRIUM did: parsing, geocoding source, validation steps, exports, and where the deliverables landed. Citable in a grant or audit.',
  dictionary: 'Lists every column from your upload with its inferred data type and a sample value. Helpful when the receiver was not the one who built the source file.',
  quality: 'The 11-step QA report card with PASS / WARN / FAIL per rule. This is the page that proves the data is fit for use.',
  stats: 'Mean, median, min, and max of the selected value column. Quick numeric snapshot before the spatial detail.',
  maps: 'A real PNG of all geocoded points on the basemap you choose, with each point optionally labelled by a column you pick.',
  hotspots: 'A ranked table of the highest-value records. You choose which columns to display via the mapping step.',
  jurisdiction: 'A breakdown of record counts grouped by the category column you mapped (e.g. tenant slug, district, ward).',
  rawdata: 'The first 100 rows of your geocoded dataset rendered as a table. Useful for spot-checking inside the document.',
  references: 'Citations for the geocoder, basemap, format standards, and MAPTRIUM methodology.',
  disclosure: 'Legal footer and generation timestamp. Always included.',
};

export default function NewJob() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);

  // Step 1 - onboard
  const [client, setClient] = useState({ name: '', contact_email: '', jurisdiction: '', project_type: 'crash_safety' });
  const [projectTypes, setProjectTypes] = useState(null);
  const selectedType = (projectTypes || []).find(t => t.key === client.project_type);
  const [showDataReq, setShowDataReq] = useState(false);

  // Step 2 - upload
  const [file, setFile] = useState(null);
  const [profile, setProfile] = useState(null);    // { columns, sample, row_count }
  const [job, setJob] = useState(null);

  // Maptrium mode — LiDAR + drone pipelines. Off by default; the user picks
  // a mode from the dropdown and the upload flow routes through maptrium-core
  // instead of the legacy data-ingest pipeline. Default 'off' = legacy flow.
  const [maptriumMode, setMaptriumMode] = useState('off');   // off | drone | lidar | survey | ortho
  const [maptriumQuality, setMaptriumQuality] = useState('standard'); // fast | standard | full
  const [maptriumResult, setMaptriumResult] = useState(null);
  const [maptriumPreflight, setMaptriumPreflight] = useState(null);
  // Worker status for the Maptrium path. `online` is null while loading, true/false after probe.
  const [maptriumStatus, setMaptriumStatus] = useState({ online: null, reason: null, version: null });
  const isMaptrium = maptriumMode !== 'off';

  // Probe the Maptrium worker on mount + whenever the user picks a non-off mode.
  // Cheap: ~3s timeout, 1 call. Keeps the user from queuing a job the worker
  // can't service (e.g. workstation off, tunnel down).
  useEffect(() => {
    if (!isMaptrium) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/maptrium-status');
        const data = await r.json();
        if (!cancelled) setMaptriumStatus(data);
      } catch (e) {
        if (!cancelled) setMaptriumStatus({ online: false, reason: 'network_error' });
      }
    })();
    return () => { cancelled = true; };
  }, [isMaptrium, maptriumMode]);

  // Step 3 - mapping
  const [mapping, setMapping] = useState({
    location_kind: 'address',
    lat_col: '', lng_col: '',
    address_col: '',
    street_col: '', city_col: '', state_col: '', zip_col: '',
    label_col: '', value_col: '', category_col: '',
    hotspot_columns: [],          // operator picks which columns appear in the hotspot table
    point_label_col: '',          // which column to use as the visible map-point label (defaults to label_col)
    basemap: 'World_Topo_Map',    // basemap used for the report map exhibit
    hotspot_sort: '',             // '', 'desc' (highest first), 'asc' (lowest first). Empty = auto.
  });

  // Step 4 - outputs
  const [formats, setFormats] = useState({ geojson: true, kml: true, csv: true, shapefile: true, fgdb: false, pptx: false });
  // PPTX briefing deck options — only matter when formats.pptx is on.
  const [pptxMapTarget, setPptxMapTarget] = useState('agol'); // 'agol' | 'mapbox' | 'tenant'
  const [pptxTenantUrl, setPptxTenantUrl] = useState('');
  const [planGroup, setPlanGroup] = useState('solo');
  useEffect(() => {
    api.me().then(m => {
      const t = (m?.user?.tier || m?.tier || 'solo').toLowerCase();
      setPlanGroup(t); // tier strings already map to plan_group keys
    }).catch(() => {});
  }, []);
  const [sections, setSections] = useState(Object.fromEntries(REPORT_SECTIONS.map(s => [s.key, !!s.default || !!s.locked])));
  const [publishAgol, setPublishAgol] = useState(false);
  const [agolState, setAgolState] = useState({ has_password: false, org_url: '', username: '' });

  // Step 5 - run
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState([]);
  // Per-user feature flag. Backend env var PIPELINE_V2_EMAILS allowlists
  // specific accounts (currently Brief Demo) onto the 5-agent pipeline v2.
  // The flag is fetched once on mount; everyone else stays on v1.
  const [pipelineV2, setPipelineV2] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  // Step 3 preview-map state — user can render a map with current mapping
  // + symbology before committing to the full pipeline run.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState('');

  // Load my AGOL state once so the Run step can show the right toggle
  useEffect(() => {
    api.getAgol().then(d => setAgolState(d.agol || {})).catch(() => {});
  }, []);

  // Load project-type registry (industry-standard presets + glossary)
  useEffect(() => {
    api.projectTypes().then(d => setProjectTypes(d.types || [])).catch(() => setProjectTypes([]));
  }, []);

  // Pipeline v2 flag (5-agent stack — Brief Demo + admins only for now)
  useEffect(() => {
    api.getFlags().then(d => setPipelineV2(!!d?.flags?.pipeline_v2)).catch(() => setPipelineV2(false));
  }, []);

  // When project type changes, apply its symbology preset + suggested report sections.
  // Only stomp values the user hasn't customized.
  useEffect(() => {
    if (!projectTypes || !selectedType) return;
    const preset = selectedType.symbology_preset || {};
    setMapping(m => ({
      ...m,
      // Symbology preset cascades only if symbology hasn't been customized yet
      symbology: m.symbology && m.symbology._user_touched ? m.symbology : {
        palette: preset.palette,
        shape: preset.shape,
        icon: preset.icon,
        size: preset.size,
        stroke_color: preset.stroke_color,
        stroke_width: preset.stroke_width,
        fill_alpha: 1,
        color_by: null,        // user picks the actual column at mapping step
        categories: { ...(preset.categories_hint || {}) },
        size_by: null,
        size_min: 6,
        size_max: 22,
        icon_color: '#FFFFFF',
        _preset_applied: selectedType.key,
      },
    }));
    // Sections preset — only apply if user hasn't toggled them yet
    if (selectedType.report_sections && selectedType.report_sections.length > 0) {
      setSections(s => {
        if (s._user_touched) return s;
        const next = {};
        for (const k of REPORT_SECTIONS.map(r => r.key)) next[k] = false;
        for (const k of selectedType.report_sections) next[k] = true;
        return next;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.project_type, projectTypes]);

  const advance = () => setStep(s => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep(s => Math.max(0, s - 1));

  const submitOnboard = async () => {
    setErr('');
    try {
      const j = await api.createJob({ client });
      setJob(j.job);
      advance();
    } catch (e) { setErr(e.message); }
  };

  const [uploading, setUploading] = useState(false);
  const submitUpload = async () => {
    setErr('');
    if (!file) return setErr('Pick a file to continue.');
    setUploading(true);
    try {
      if (isMaptrium) {
        // Maptrium path: direct-to-blob SAS upload (no 50 MB cap), skip CSV
        // profile step, go straight to the maptrium-core dispatch.
        const sas = await api.uploadSas(job.id, file.name);
        await api.uploadToSas(sas.upload_url, file);
        // Optional preflight: project_type auto-detect + ETA
        try {
          const check = await api.checkMaptrium(job.id, {
            type: 'fused',
            options: { project_type: maptriumMode, quality: maptriumQuality },
          });
          setMaptriumPreflight(check);
        } catch { /* preflight is advisory; don't block */ }
        const res = await api.runMaptrium(job.id, {
          maptrium_type: maptriumMode, quality: maptriumQuality,
        });
        setMaptriumResult(res);
        setStep(5);  // jump to a maptrium result pane
      } else {
        await api.uploadFile(job.id, file);
        const p = await api.profileFile(job.id);
        setProfile(p);
        if (p?.suggested_mapping) setMapping(m => ({ ...m, ...p.suggested_mapping }));
        advance();
      }
    } catch (e) { setErr(e.message); }
    finally { setUploading(false); }
  };

  const submitMapping = async () => {
    setErr('');
    try {
      await api.setMapping(job.id, mapping);
      advance();
    } catch (e) { setErr(e.message); }
  };

  // Merge Ali's output into the wizard state — column mapping, symbology,
  // AND section hints. mapping fields Ali produces overwrite any prior
  // guesses; the user can still hand-tweak below.
  const applyAli = ({ symbology, mapping: aliMapping, section_hints }) => {
    setMapping(m => ({
      ...m,
      ...(aliMapping || {}),
      symbology: { ...(m.symbology || {}), ...(symbology || {}), _user_touched: true },
      section_hints: { ...(m.section_hints || {}), ...(section_hints || {}) },
    }));
  };

  // Preview map — runs profile → geocoder → symbology → PNG. Caches
  // geocoded rows on the job so re-previews and the full run are free.
  //
  // overrides: pass Ali's proposed { symbology, mapping } to preview her
  // picks WITHOUT touching wizard state. When omitted, uses saved wizard
  // state (the standard "Preview map" button behavior).
  const runPreview = async ({ force = false, overrides = null } = {}) => {
    if (!job?.id) return;
    setPreviewError(''); setPreviewLoading(true); setPreviewData(null); setPreviewOpen(true);
    try {
      const previewMapping = overrides?.mapping ? { ...mapping, ...overrides.mapping } : mapping;
      const previewSymb    = overrides?.symbology || mapping.symbology;
      // Save current wizard mapping (not Ali overrides) so wizard state stays intact
      if (!overrides) await api.setMapping(job.id, mapping).catch(() => {});
      const res = await api.previewMap(job.id, {
        symbology: previewSymb,
        mapping: previewMapping,
        force_regeocode: force,
      });
      setPreviewData(res);
    } catch (e) {
      setPreviewError(e?.message || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const submitRun = async () => {
    setErr(''); setRunning(true); setProgress([]);
    const chosenFormats = Object.entries(formats).filter(([_, v]) => v).map(([k]) => k);
    const pptxOpts = formats.pptx ? { map_link_target: pptxMapTarget, tenant_url: pptxTenantUrl.trim() } : null;
    const chosenSections = Object.entries(sections).filter(([_, v]) => v).map(([k]) => k);
    try {
      // Brief Demo + admins go to the 5-agent v2 pipeline. Everyone else
      // stays on v1. Both endpoints return the same shape; v2 ADDS
      // agent_lineage + intake_warnings on top.
      const runFn = pipelineV2 ? api.runJobV2 : api.runJob;
      const res = await runFn(job.id, { formats: chosenFormats, sections: chosenSections, pptx_options: pptxOpts });
      if (res.progress) setProgress(res.progress);
      setResult(res);
      // Optional AGOL publish after main pipeline (best effort, surfaced inline)
      if (publishAgol && agolState.has_password) {
        try {
          const p = await api.publishAgol(job.id);
          setResult(r => ({ ...r, agol: p }));
        } catch (e) {
          // PAYG users hit a 402 with addon checkout details — kick into Stripe.
          if (e.status === 402 && e.data?.addon) {
            try {
              const ck = await api.addonCheckout(job.id, e.data.addon);
              if (ck?.url) {
                setResult(r => ({ ...r, agol_addon_pending: { addon: e.data.addon, price_usd: e.data.addon_price_usd, checkout_url: ck.url } }));
                // Open Stripe in a new tab so they can return to this job after paying.
                window.open(ck.url, '_blank', 'noopener');
                return;
              }
            } catch (ce) { setResult(r => ({ ...r, agol_error: ce.message })); return; }
          }
          setResult(r => ({ ...r, agol_error: e.message }));
        }
      }
    } catch (e) {
      // PAYG users blocked at the run step (e.g. requested shapefile but didn't pay).
      if (e.status === 402 && Array.isArray(e.data?.blocked)) {
        setResult({ payg_blocked: e.data });
      } else if (e.data?.failure) {
        // Structured failure from the backend — render the WHAT/WHY/WHAT-TO-DO block.
        setResult({ failure: e.data.failure, progress: e.data.progress || [] });
        if (e.data.progress) setProgress(e.data.progress);
      } else {
        setErr(e.message);
      }
    } finally {
      setRunning(false);
    }
  };

  const purchaseAddon = async (addon) => {
    try {
      const ck = await api.addonCheckout(job.id, addon);
      if (ck?.url) window.open(ck.url, '_blank', 'noopener');
    } catch (e) { setErr(e.message); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-10">
        <div className="mb-6 md:mb-8">
          <div className="tag tag-gold mb-2">New job</div>
          <h1 className="text-2xl md:text-3xl font-bold text-navy">Run a map</h1>
          <p className="text-ink-muted text-sm mt-1">Five steps. Five minutes. One delivery package.</p>
        </div>

        {/* Mobile stepper: compact "Step N of 5 — Label" chip */}
        <div className="md:hidden mb-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-navy text-white font-bold text-sm flex items-center justify-center flex-shrink-0">
              {step + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-ink-muted">Step {step + 1} of {STEPS.length}</div>
              <div className="text-sm font-semibold text-navy truncate">{STEPS[step]}</div>
            </div>
            <div className="text-xs text-ink-muted flex-shrink-0">{Math.round(((step + 1) / STEPS.length) * 100)}%</div>
          </div>
          <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
          </div>
        </div>

        {/* Desktop stepper: full labeled row (unchanged behavior) */}
        <ol className="hidden md:flex items-center gap-2 mb-8">
          {STEPS.map((label, i) => (
            <li key={i} className="flex-1 flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full font-bold text-xs flex items-center justify-center ${i < step ? 'bg-emerald-500 text-white' : i === step ? 'bg-navy text-white' : 'bg-slate-200 text-ink-muted'}`}>
                {i < step ? '✓' : i + 1}
              </div>
              <div className={`text-xs font-semibold ${i === step ? 'text-navy' : 'text-ink-muted'}`}>{label}</div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? 'bg-emerald-500' : 'bg-slate-200'}`} />}
            </li>
          ))}
        </ol>

        {err && <div className="card card-pad mb-6 border-red-200 bg-red-50 text-red-700 text-sm">{err}</div>}

        {/* Step bodies */}
        {step === 0 && (
          <div className="card card-pad space-y-4">
            <h2 className="font-semibold text-navy">Step 1: Onboard your client</h2>
            <p className="text-sm text-ink-muted">A dedicated client workspace is provisioned with reference documents.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="label">Client / organization</label><input className="input" value={client.name} onChange={e => setClient({ ...client, name: e.target.value })} placeholder="City of Martinsville" /></div>
              <div><label className="label">Contact email</label><input className="input" type="email" value={client.contact_email} onChange={e => setClient({ ...client, contact_email: e.target.value })} placeholder="ops@martinsville-va.gov" /></div>
              <div><label className="label">Jurisdiction (state)</label><input className="input" value={client.jurisdiction} onChange={e => setClient({ ...client, jurisdiction: e.target.value })} placeholder="VA" /></div>
              <div>
                <label className="label flex items-center gap-1">
                  Project type
                  <InfoBadge title="Project type drives presets" body={'Each type pre-loads industry-standard symbology, report sections, and naming conventions: FHWA HSIP/KABCO for crash safety, APA color codes for planning, MLS conventions for real estate, ISO PPC + NFIP/FEMA zones for insurance, EPA EJScreen for environmental, APWA + GASB-34 for public works. You can override anything after the preset is applied.'} />
                </label>
                <select className="input" value={client.project_type} onChange={e => setClient({ ...client, project_type: e.target.value })}>
                  {(projectTypes || []).map(t => (
                    <option key={t.key} value={t.key}>{t.label}</option>
                  ))}
                </select>
                {selectedType && (
                  <div className="mt-1 text-xs text-ink-muted">
                    <span className="font-semibold text-navy">Standard:</span> {selectedType.industry_standard}<br />
                    <span className="font-semibold text-navy">Naming:</span> <code>{selectedType.naming_convention}</code>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end">
              <button className="btn btn-primary" disabled={!client.name || !client.contact_email} onClick={submitOnboard}>Onboard + Continue →</button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="card card-pad space-y-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <h2 className="font-semibold text-navy">Step 2: Upload data</h2>
                <p className="text-sm text-ink-muted">CSV, Excel, GeoJSON, KML, or ZIP'd Shapefile. Max 50 MB.</p>
              </div>
              <div className="flex gap-2">
                <button type="button" className="btn btn-ghost border border-slate-300" onClick={() => setShowDataReq(true)}>
                  ⓘ What does my file need?
                </button>
                <a href="/api/data-requirements.docx" className="btn btn-ghost border border-slate-300" download>
                  ↓ Data Prep Guide (.docx)
                </a>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
              <strong>Minimum the system needs to create a map point:</strong> ONE column for location. Either lat+lng coordinates, a single full-address column, separate street+city+state columns, or a ZIP code. <a href="#" className="font-semibold underline" onClick={(e) => { e.preventDefault(); setShowDataReq(true); }}>See full requirements →</a>
            </div>
            {/* Maptrium pipeline switch — opt-in LiDAR / drone / fused path.
                Default 'Off (classic data upload)' preserves 100% of the
                legacy wizard flow. */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-semibold text-navy">Pipeline</span>
                <InfoBadge title="Classic vs. Maptrium pipelines"
                           body={'Classic: your CSV/Excel/GeoJSON goes through the standard mapping + report pipeline (no change). Maptrium: new path for LiDAR point clouds, drone photo sets, and orthoimagery — produces orthomosaics, point clouds, DSM/DTM, and AI feature detection.'} />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="label">Mode</label>
                  <select className="input" value={maptriumMode}
                          onChange={e => setMaptriumMode(e.target.value)}>
                    <option value="off">Off — classic data upload (CSV / Excel / GeoJSON)</option>
                    <option value="drone">🛸 Drone mapping (photos → orthomosaic + point cloud)</option>
                    <option value="lidar">📡 LiDAR processing (LAS/LAZ → classified COPC)</option>
                    <option value="survey">🗺️ Both / mixed (drone + LiDAR → fused)</option>
                    <option value="ortho">🧠 AI detection on existing ortho/image</option>
                  </select>
                </div>
                {isMaptrium && (
                  <div>
                    <label className="label">Quality</label>
                    <select className="input" value={maptriumQuality}
                            onChange={e => setMaptriumQuality(e.target.value)}>
                      <option value="fast">Fast (~30 sec, orthomosaic only)</option>
                      <option value="standard">Standard (~5 min, incl. point cloud)</option>
                      <option value="full">Full (~15 min, incl. DSM/DTM/mesh)</option>
                    </select>
                  </div>
                )}
              </div>
              {isMaptrium && (
                <div className="mt-3 text-xs text-slate-700 bg-amber-50 border border-amber-200 rounded p-2 space-y-1">
                  <div>Maptrium path: accepts JPG / TIFF / LAS / LAZ / E57 / PLY / zip. No 50 MB cap. Results include real geo-referenced detections.</div>
                  {(maptriumMode === 'lidar' || maptriumMode === 'survey') && (
                    <div><strong>LiDAR DEM:</strong> output produced at <strong>1.0 m / pixel</strong> resolution. Sub-meter rasters are on the roadmap via chunked tiling &mdash; <a href="mailto:info@atlasgeo.ai?subject=MAPTRIUM%20sub-meter%20LiDAR" className="underline text-navy">contact us</a> for high-density delivery.</div>
                  )}
                </div>
              )}
              {isMaptrium && maptriumStatus.online === false && (
                <div className="mt-3 text-xs bg-red-50 border border-red-200 rounded p-2">
                  <strong className="text-red-800">Maptrium worker offline.</strong>
                  <span className="text-red-700"> Jobs cannot run right now ({maptriumStatus.reason || 'unreachable'}). The GPU worker that powers Drone / LiDAR / AI pipelines is unreachable. Please contact support or try again in a few minutes.</span>
                </div>
              )}
              {isMaptrium && maptriumStatus.online === null && (
                <div className="mt-3 text-xs text-slate-500">Checking Maptrium worker status…</div>
              )}
              {isMaptrium && maptriumStatus.online === true && (
                <div className="mt-3 text-xs text-emerald-700">✓ Maptrium worker online{maptriumStatus.version ? ` (${maptriumStatus.version})` : ''}.</div>
              )}
            </div>

            <label className="block border-2 border-dashed border-slate-300 rounded-xl p-10 text-center hover:border-navy hover:bg-slate-50 cursor-pointer transition">
              <div className="text-4xl mb-2">{isMaptrium ? '🛸' : '📂'}</div>
              <div className="font-semibold text-navy mb-1">{file ? file.name : 'Click to choose a file'}</div>
              <div className="text-xs text-ink-muted">
                {file ? `${(file.size / 1024 / 1024).toFixed(2)} MB`
                      : isMaptrium ? '.jpg, .tif, .las, .laz, .e57, .ply, .zip' : '.csv, .xlsx, .geojson, .kml, .zip'}
              </div>
              <input type="file" className="hidden"
                     accept={isMaptrium
                       ? '.jpg,.jpeg,.png,.tif,.tiff,.las,.laz,.e57,.ply,.pcd,.zip'
                       : '.csv,.xlsx,.xls,.geojson,.json,.kml,.kmz,.zip'}
                     onChange={e => setFile(e.target.files[0] || null)} />
            </label>
            <div className="flex justify-between">
              <button className="btn btn-ghost" onClick={back} disabled={uploading}>← Back</button>
              <button className="btn btn-primary" disabled={!file || uploading || (isMaptrium && maptriumStatus.online === false)} onClick={submitUpload}>
                {uploading
                  ? (isMaptrium ? 'Uploading + dispatching…' : 'Uploading…')
                  : (isMaptrium ? `Process (${maptriumMode}) →` : 'Upload + Profile →')}
              </button>
            </div>
          </div>
        )}

        {showDataReq && <DataRequirementsModal onClose={() => setShowDataReq(false)} />}

        {step === 2 && profile && (
          <div className="card card-pad space-y-4">
            <h2 className="font-semibold text-navy">Step 3: Tell us about your data</h2>
            <p className="text-sm text-ink-muted">We looked at your file and found <strong>{profile.columns?.length || 0} columns</strong> and <strong>{profile.row_count} rows</strong>. Walk through the steps below. Only Steps 1 and 5 are required. Skip the rest if you're not sure.</p>

            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900">
              <strong>👋 Tip:</strong> You only HAVE to do Steps 1 and 5. Leave the others on their defaults if you're not sure; you can always re-run a job after looking at the first result.
            </div>

            {/* Ali does the whole page in one shot — column mapping + symbology + section hints. */}
            <AliAssistPanel
              columns={profile.columns}
              projectType={client.project_type}
              clientBrief={mapping.brief}
              jobId={job?.id}
              onApply={applyAli}
              onPreview={(overrides) => runPreview({ overrides })}
            />

            <div className="text-xs text-center text-ink-muted italic">or configure each step by hand below</div>

            <StepRow num="1" required title="Where are your places?" hint="Tell us how your spreadsheet stores the location. Pick the option that matches what your file looks like.">
              <select className="input" value={mapping.location_kind} onChange={e => setMapping({ ...mapping, location_kind: e.target.value })}>
                {LOCATION_KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
              </select>
              <div className="text-xs text-ink-muted mt-1">{LOCATION_KINDS.find(k => k.key === mapping.location_kind)?.info}</div>

              {mapping.location_kind === 'latlng' && (
                <div className="grid md:grid-cols-2 gap-4 mt-3">
                  <ColSelect label="Which column has the up/down number? (latitude)" hint="Latitude is the up/down position on Earth. Looks like 37.5102. Anything in the US is positive." value={mapping.lat_col} columns={profile.columns} onChange={v => setMapping({ ...mapping, lat_col: v })} />
                  <ColSelect label="Which column has the left/right number? (longitude)" hint="Longitude is the left/right position on Earth. Looks like -76.9890. Anything in the US is negative." value={mapping.lng_col} columns={profile.columns} onChange={v => setMapping({ ...mapping, lng_col: v })} />
                </div>
              )}
              {mapping.location_kind === 'address' && (
                <div className="mt-3">
                  <ColSelect label="Which column has the full address?" hint="Pick the column that holds the whole address as one piece of text, like '100 Main St, Anytown, VA 24112'." value={mapping.address_col} columns={profile.columns} onChange={v => setMapping({ ...mapping, address_col: v })} />
                </div>
              )}
              {mapping.location_kind === 'street_city_state' && (
                <div className="grid md:grid-cols-2 gap-4 mt-3">
                  <ColSelect label="Street column" hint="Just the street number + name, like '100 Main St'." value={mapping.street_col} columns={profile.columns} onChange={v => setMapping({ ...mapping, street_col: v })} />
                  <ColSelect label="City column" hint="Just the city name, like 'Martinsville'." value={mapping.city_col} columns={profile.columns} onChange={v => setMapping({ ...mapping, city_col: v })} />
                  <ColSelect label="State column" hint="Two-letter state code, like 'VA'." value={mapping.state_col} columns={profile.columns} onChange={v => setMapping({ ...mapping, state_col: v })} />
                  <ColSelect label="ZIP column (optional)" hint="If you have a ZIP column, picking it makes lookups more accurate." value={mapping.zip_col} columns={profile.columns} onChange={v => setMapping({ ...mapping, zip_col: v })} allowEmpty />
                </div>
              )}
              {mapping.location_kind === 'zip' && (
                <div className="mt-3">
                  <ColSelect label="Which column has the ZIP code?" hint="5-digit ZIP, like 24112." value={mapping.zip_col} columns={profile.columns} onChange={v => setMapping({ ...mapping, zip_col: v })} />
                </div>
              )}
            </StepRow>

            <StepRow num="2" optional title="What should each pin be called?" hint="Pick a column that has short names or IDs. This shows up next to each dot on the map and in the ranked table. Leave blank and we'll use the address.">
              <ColSelect label="" hint={null} value={mapping.label_col} columns={profile.columns} onChange={v => setMapping({ ...mapping, label_col: v })} allowEmpty />
            </StepRow>

            <StepRow num="3" optional title='Any "how big" or "how bad" number?' hint='If your rows have a number that means "more important", pick that column. Examples: price, severity, crash count, score. MAPTRIUM uses it to rank the table.'>
              <ColSelect label="" hint={null} value={mapping.value_col} columns={profile.columns} onChange={v => setMapping({ ...mapping, value_col: v })} allowEmpty />
            </StepRow>

            <StepRow num="4" optional title='Are your rows grouped by a "type"?' hint='If your rows have categories like "fire truck" vs "aid station" or "fatal" vs "minor", pick that column. We use it to color the dots and add a per-category breakdown.'>
              <ColSelect label="" hint={null} value={mapping.category_col} columns={profile.columns} onChange={v => setMapping({ ...mapping, category_col: v })} allowEmpty />
            </StepRow>

            <StepRow num="5" required title="Which columns should the report's ranked table show?" hint="Tick the columns you want to see in the Top Hotspots table. Pick the ones that matter most to whoever reads the report. If you leave everything blank, we'll just show the Label and Value columns.">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1 mt-1 max-h-44 overflow-y-auto p-2 bg-slate-50 rounded border border-slate-200">
                {(profile.columns || []).map(c => (
                  <label key={c.name} className="flex gap-2 items-center text-xs">
                    <input
                      type="checkbox"
                      checked={mapping.hotspot_columns.includes(c.name)}
                      onChange={e => {
                        const next = e.target.checked
                          ? Array.from(new Set([...mapping.hotspot_columns, c.name]))
                          : mapping.hotspot_columns.filter(x => x !== c.name);
                        setMapping({ ...mapping, hotspot_columns: next });
                      }}
                    />
                    <span className="font-mono">{c.name}</span>
                    <span className="text-ink-muted">({c.type})</span>
                  </label>
                ))}
              </div>
              <div className="text-xs text-ink-muted mt-1">Showing: {mapping.hotspot_columns.length === 0 ? <em>Just the basics (Label + Value)</em> : mapping.hotspot_columns.join(', ')}</div>
              <div className="mt-3">
                <label className="label flex items-center gap-1">Should the biggest number show first, or the smallest?
                  <InfoBadge title="Sort order" body={'Pick "Biggest first" when the number means "more important" (price, severity, count). Pick "Smallest first" when the number is a rank where 1 means best. "Auto" picks Biggest unless the column is named "rank".'} />
                </label>
                <select className="input" value={mapping.hotspot_sort} onChange={e => setMapping({ ...mapping, hotspot_sort: e.target.value })}>
                  <option value="">Auto (pick automatically)</option>
                  <option value="desc">Biggest number first</option>
                  <option value="asc">Smallest number first (use when the column is a rank, 1 = best)</option>
                </select>
              </div>
            </StepRow>

            <StepRow num="6" optional title="What text should float next to each pin?" hint="Pick a column with SHORT values like IDs, ranks, or one-word names. Long text gets cut off and overlaps. Leave blank and we'll fall back to the Label column.">
              <div className="grid md:grid-cols-2 gap-4">
                <ColSelect label="" hint={null} value={mapping.point_label_col} columns={profile.columns} onChange={v => setMapping({ ...mapping, point_label_col: v })} allowEmpty />
                <div>
                  <label className="label flex items-center gap-1">What background should the map have?
                    <InfoBadge title="Background style" body={'The picture under your dots. Topographic = roads + hills. Streets = roads only. Satellite = aerial photos. Light/Dark gray = almost-blank so your dots pop.'} />
                  </label>
                  <select className="input" value={mapping.basemap} onChange={e => setMapping({ ...mapping, basemap: e.target.value })}>
                    {BASEMAPS.map(b => <option key={b.key} value={b.key}>{b.label}: {b.desc}</option>)}
                  </select>
                </div>
              </div>
            </StepRow>

            <StepRow num="7" optional title="Tell us about the project. Atlas writes the report from this." hint="A few sentences about what this map is for, who's reading it, and what matters. MAPTRIUM puts your words into a 'Project Context' section near the top of the Word report. Leave blank if you don't have anything to say.">
              <ProjectBriefPanel
                brief={mapping.brief}
                onChange={b => setMapping({ ...mapping, brief: b })}
                noWrapper
              />
            </StepRow>

            <StepRow num="8" optional title="How should the dots look on the map?" hint="Pick a color set, dot shape, size, and (optional) icon. If you skip this, MAPTRIUM uses Atlas Geo navy + gold dots. They look good on every basemap.">
              <SymbologyPicker
                columns={profile.columns}
                symbology={mapping.symbology}
                onChange={s => setMapping({ ...mapping, symbology: s })}
                noWrapper
              />
            </StepRow>

            <details className="mt-2">
              <summary className="cursor-pointer text-sm font-semibold text-navy">Preview sample rows</summary>
              <SamplePreview profile={profile} />
            </details>

            <div className="flex justify-between items-center flex-wrap gap-2">
              <button className="btn btn-ghost" onClick={back}>← Back</button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="btn btn-gold"
                  onClick={() => runPreview({ force: false })}
                  disabled={previewLoading}
                  title="Render a preview map with your current mapping + symbology. First preview geocodes; re-previews are free."
                >
                  {previewLoading ? 'Rendering preview…' : '🗺 Preview map'}
                </button>
                <button className="btn btn-primary" onClick={submitMapping}>Save mapping + Continue →</button>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div className="card card-pad space-y-4">
              <h2 className="font-semibold text-navy flex items-center gap-1">
                Step 4a: Choose export formats
                <InfoBadge title="Export formats" body={'Each format you tick generates a separate file in your deliverable bundle. They’re all built from the same geocoded dataset, so you can safely tick everything if you’re unsure who will consume the data.'} />
              </h2>
              <div className="grid md:grid-cols-2 gap-3">
                {FORMATS.map(f => (
                  <label key={f.key} className={`flex gap-3 items-start p-3 rounded-lg border-2 cursor-pointer ${formats[f.key] ? 'border-navy bg-navy/5' : 'border-slate-200'}`}>
                    <input type="checkbox" checked={!!formats[f.key]} onChange={e => setFormats({ ...formats, [f.key]: e.target.checked })} className="mt-1" />
                    <div className="flex-1">
                      <div className="font-semibold text-navy flex items-center">
                        {f.label}
                        {f.info && <InfoBadge title={f.label} body={f.info} />}
                      </div>
                      <div className="text-xs text-ink-muted">{f.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* PPTX briefing deck — opt-in. Tier-gated price chip via /api/me. */}
            <div className="card card-pad space-y-3">
              <h2 className="font-semibold text-navy flex items-center gap-1">
                Step 4a-2: Briefing deck (.pptx)
                <InfoBadge title="PowerPoint briefing deck" body={'Generates a 13-slide briefing PowerPoint deck with cover, executive summary, KPI scorecard, corridor map, hotspot maps, recommendations, and methodology. Map slides are clickable. They always show a picture of the map and open a live web map when clicked, and on PowerPoint 2019 or newer they render a live map iframe inside the slide.'} />
              </h2>
              <label className={`flex gap-3 items-start p-3 rounded-lg border-2 cursor-pointer ${formats.pptx ? 'border-navy bg-navy/5' : 'border-slate-200 hover:border-gold'}`}>
                <input type="checkbox" checked={!!formats.pptx} onChange={e => setFormats({ ...formats, pptx: e.target.checked })} className="mt-1" />
                <div className="flex-1">
                  <div className="font-semibold text-navy flex items-center justify-between">
                    <span>Add a PowerPoint briefing deck</span>
                    <PptxPriceChip planGroup={planGroup} />
                  </div>
                  <div className="text-xs text-ink-muted mt-1">Adds a 13-slide deck to the deliverable bundle. Included free on Professional, Business, and Enterprise.</div>
                </div>
              </label>

              {formats.pptx && (
                <div className="ml-7 space-y-3 border-l-2 border-gold/30 pl-4">
                  <div className="text-sm font-semibold text-navy">Choose how the live map link works</div>
                  <label className="flex gap-2 items-start text-sm cursor-pointer">
                    <input type="radio" name="pptxMap" value="agol" checked={pptxMapTarget === 'agol'} onChange={() => setPptxMapTarget('agol')} className="mt-1" />
                    <div><div className="font-medium">Use my ArcGIS Online map</div><div className="text-xs text-ink-muted">Default if you have AGOL credentials saved in Settings.</div></div>
                  </label>
                  <label className="flex gap-2 items-start text-sm cursor-pointer">
                    <input type="radio" name="pptxMap" value="mapbox" checked={pptxMapTarget === 'mapbox'} onChange={() => setPptxMapTarget('mapbox')} className="mt-1" />
                    <div><div className="font-medium">Use a plain web map (Mapbox)</div><div className="text-xs text-ink-muted">Works without AGOL.</div></div>
                  </label>
                  <label className="flex gap-2 items-start text-sm cursor-pointer">
                    <input type="radio" name="pptxMap" value="tenant" checked={pptxMapTarget === 'tenant'} onChange={() => setPptxMapTarget('tenant')} className="mt-1" />
                    <div className="flex-1">
                      <div className="font-medium">Use my own map URL</div>
                      {pptxMapTarget === 'tenant' && (
                        <input
                          type="url"
                          className="w-full mt-1 border border-slate-300 rounded px-2 py-1 text-sm"
                          placeholder="https://yourcity.maps.arcgis.com/apps/webappviewer/..."
                          value={pptxTenantUrl}
                          onChange={e => setPptxTenantUrl(e.target.value)}
                        />
                      )}
                      <div className="text-xs text-ink-muted mt-1">Must start with https://. Audience clicks the map slide to open this URL.</div>
                    </div>
                  </label>
                  <div className="text-xs text-gold-700 italic">
                    Tip: the deck always shows a picture of the map. The "live" part lights up when your audience opens it in PowerPoint 2019 or newer with internet.
                  </div>
                </div>
              )}
            </div>

            <div className="card card-pad space-y-4">
              <h2 className="font-semibold text-navy flex items-center gap-1">
                Step 4b: Report sections (DOCX)
                <InfoBadge title="Report sections" body={'Each ticked section becomes a heading in the DOCX. Two sections (cover and disclosure) are mandatory. Click the (?) next to any item for what that section contains.'} />
              </h2>
              <div className="grid md:grid-cols-2 gap-2">
                {REPORT_SECTIONS.map(s => (
                  <label key={s.key} className={`flex gap-2 items-start p-2 rounded ${s.locked ? 'bg-gold/10' : 'hover:bg-slate-50'}`}>
                    <input type="checkbox" disabled={s.locked} checked={!!sections[s.key]} onChange={e => setSections({ ...sections, [s.key]: e.target.checked })} className="mt-1" />
                    <div className="flex-1">
                      <div className="text-sm flex items-center">
                        {s.label}
                        {REPORT_SECTION_INFO[s.key] && <InfoBadge title={s.label} body={REPORT_SECTION_INFO[s.key]} />}
                      </div>
                      {s.locked && <div className="text-[10px] text-gold-600 uppercase tracking-widest">Mandatory</div>}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="card card-pad space-y-3">
              <h2 className="font-semibold text-navy flex items-center gap-1">
                Step 4c: Publish to ArcGIS Online
                <InfoBadge title="Publish to your AGOL" body={'Optional. If your account has ArcGIS Online credentials saved, ticking this also publishes the GeoJSON as a hosted Feature Layer in YOUR AGOL org after the main pipeline finishes. You can do this later from the job detail too.'} />
              </h2>
              {agolState.has_password ? (
                <label className="flex gap-3 items-start p-3 rounded-lg border-2 border-slate-200 cursor-pointer hover:border-gold">
                  <input type="checkbox" checked={publishAgol} onChange={e => setPublishAgol(e.target.checked)} className="mt-1" />
                  <div>
                    <div className="font-semibold text-navy">Publish to {agolState.username} @ {agolState.org_url || 'arcgis.com'}</div>
                    <div className="text-xs text-ink-muted">Creates a hosted Feature Layer in your AGOL org and links it from the job detail.</div>
                  </div>
                </label>
              ) : (
                <div className="text-sm text-ink-muted bg-slate-50 rounded p-3 border border-slate-200">
                  AGOL is not yet configured on your account.{' '}
                  <a href="/settings/agol" target="_blank" rel="noopener noreferrer" className="text-navy font-semibold underline">Set it up in Settings ↗</a>{' '}
                  (opens new tab, your wizard progress is preserved). After saving, click{' '}
                  <button
                    type="button"
                    onClick={() => api.getAgol().then(d => setAgolState(d.agol || {})).catch(() => {})}
                    className="text-navy font-semibold underline"
                  >Refresh</button>{' '}to pick it up.
                </div>
              )}
            </div>

            <div className="flex justify-between">
              <button className="btn btn-ghost" onClick={back}>← Back</button>
              <button className="btn btn-gold" onClick={() => { advance(); submitRun(); }}>Run pipeline →</button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="card card-pad space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-navy">Step 5: Workflow progress</h2>
              {pipelineV2 && (
                <span
                  className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded"
                  style={{ backgroundColor: '#DBC078', color: '#032953' }}
                  title="This run is using the new 5-agent pipeline. Agent lineage will appear after completion."
                >
                  Pipeline v2 · 5-agent stack
                </span>
              )}
            </div>
            <PipelineProgress running={running} progress={progress} result={result} />
            {/* Pipeline v2 intake soft-warn panel — only renders when the
                intake agent flagged missing required/recommended columns. */}
            {result && result.intake_warnings && (
              <IntakeWarningPanel warnings={result.intake_warnings} />
            )}
            {result && result.delivery && (
              <DeliveryCard delivery={result.delivery} sentinel={result.sentinel} onDone={() => nav(`/jobs/${job.id}`)} />
            )}
            {result && result.failure && (
              <FailureCard failure={result.failure} onRetry={submitRun} onBack={() => setStep(2)} />
            )}
            {result && !result.delivery && !result.failure && !running && (
              <button className="btn btn-primary" onClick={submitRun}>Retry pipeline</button>
            )}
          </div>
        )}

        {step === 5 && maptriumResult && (
          <div className="card card-pad space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-navy">Maptrium — {maptriumResult.maptrium_type} pipeline</h2>
              <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded"
                    style={{ backgroundColor: '#DBC078', color: '#032953' }}>
                Maptrium Core
              </span>
            </div>
            <div className="text-sm text-ink-muted">
              Dispatcher job id: <code>{maptriumResult.dispatcher_job_id}</code>
              {maptriumResult.steps && <> · Steps: <code>{maptriumResult.steps.join(' → ')}</code></>}
            </div>
            {maptriumPreflight?.estimated_runtime_human && (
              <div className="text-sm">
                <strong>Estimated runtime:</strong> {maptriumPreflight.estimated_runtime_human}
                {maptriumPreflight.warnings?.length > 0 && (
                  <ul className="mt-2 text-xs text-amber-700 list-disc list-inside">
                    {maptriumPreflight.warnings.map((w, i) => <li key={i}>{w.message}</li>)}
                  </ul>
                )}
              </div>
            )}
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-700">
              Your job is queued. Results will arrive via the Maptrium callback — refresh the job detail page to see artifacts (orthomosaic, point cloud, detections GeoJSON) when complete.
            </div>
            <div className="flex justify-end">
              <button className="btn btn-primary" onClick={() => nav(`/jobs/${job.id}`)}>Go to job detail →</button>
            </div>
          </div>
        )}
      </main>

      {previewOpen && (
        <PreviewMapModal
          loading={previewLoading}
          data={previewData}
          error={previewError}
          onRefresh={() => runPreview({ force: true })}
          onClose={() => { setPreviewOpen(false); setPreviewData(null); setPreviewError(''); }}
          onEdit={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}

// Full-screen preview modal — shows the map PNG produced by /api/jobs/:id/preview-map.
// User can Edit (close, go back to Step 3), Refresh (force re-geocode), or Continue.
function PreviewMapModal({ loading, data, error, onClose, onRefresh, onEdit }) {
  const hitPct = data?.geocode?.hit_rate != null ? Math.round(data.geocode.hit_rate * 100) : null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[92vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b bg-navy text-white flex items-center gap-3">
          <span className="text-xl">🗺</span>
          <div className="flex-1">
            <div className="font-semibold">Preview map</div>
            <div className="text-xs opacity-80">Runs geocode + symbology only. No report generated, no delivery.</div>
          </div>
          <button className="text-white/80 hover:text-white text-2xl leading-none" onClick={onClose}>×</button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          {loading && (
            <div className="text-center py-16">
              <div className="text-navy font-semibold">Rendering preview…</div>
              <div className="text-xs text-ink-muted mt-1">First preview geocodes your rows (~1-3s). Re-previews are instant.</div>
            </div>
          )}
          {error && (
            <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded p-3">{error}</div>
          )}
          {data?.preview?.data_url && (
            <>
              <img
                src={data.preview.data_url}
                alt="Preview map"
                className="mx-auto rounded-lg border border-slate-300 shadow"
                style={{ maxHeight: '55vh' }}
              />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <PreviewStat label="Rows placed" value={data.geocode?.row_count ?? 'n/a'} />
                <PreviewStat label="Geocode hit" value={hitPct != null ? `${hitPct}%` : 'n/a'} />
                <PreviewStat label="Cache" value={data.cache_hit ? 'reused (free)' : 'fresh geocode'} />
                <PreviewStat label="Elapsed" value={`${data.elapsed_ms} ms`} />
              </div>
              <div className="text-xs text-ink-muted italic">{data.hint}</div>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t bg-slate-50 flex items-center justify-between flex-wrap gap-2">
          <button className="btn btn-ghost" onClick={onEdit}>← Edit mapping / symbology</button>
          <div className="flex items-center gap-2">
            <button className="btn btn-ghost" onClick={onRefresh} disabled={loading} title="Force fresh geocode (consumes MAPTRIUM credits from your account)">
              ↻ Force re-geocode
            </button>
            <button className="btn btn-primary" onClick={onClose}>Looks good, continue</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewStat({ label, value }) {
  return (
    <div className="bg-slate-50 rounded px-2 py-1">
      <div className="text-[10px] uppercase tracking-widest text-ink-muted">{label}</div>
      <div className="font-mono text-navy">{String(value)}</div>
    </div>
  );
}

// Step row — numbered chip + REQUIRED/OPTIONAL badge + plain-English title + tooltip.
// Used to lay out the Step-3 mapping wizard so non-GIS clients can follow it.
function StepRow({ num, title, hint, required, optional, children }) {
  return (
    <div className="pt-4 border-t border-slate-200">
      <div className="flex items-start gap-3 mb-2">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-navy text-white font-bold flex items-center justify-center text-sm">{num}</div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-navy">{title}</span>
            {required && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-red-100 text-red-700 px-2 py-0.5 rounded-full">🔴 Required</span>}
            {optional && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-slate-100 text-ink-muted px-2 py-0.5 rounded-full">⚫ Optional</span>}
            {hint && <InfoBadge title={title} body={hint} />}
          </div>
        </div>
      </div>
      <div className="pl-11">{children}</div>
    </div>
  );
}

function ColSelect({ label, value, columns, onChange, allowEmpty, hint }) {
  return (
    <div>
      {label ? (
        <label className="label flex items-center gap-1">
          {label}
          {hint && <InfoBadge title={label || 'Column'} body={hint} />}
        </label>
      ) : null}
      <select className="input" value={value || ''} onChange={e => onChange(e.target.value)}>
        {allowEmpty && <option value="">Skip this / not sure</option>}
        {!allowEmpty && <option value="">Pick a column from your file…</option>}
        {(columns || []).map(c => <option key={c.name} value={c.name}>{c.name} <span>({c.type})</span></option>)}
      </select>
    </div>
  );
}

function SamplePreview({ profile }) {
  const cols = (profile.columns || []).map(c => c.name).slice(0, 8);
  const rows = (profile.sample || []).slice(0, 5);
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-xs border border-slate-200">
        <thead className="bg-slate-50">
          <tr>{cols.map(c => <th key={c} className="text-left px-2 py-1 border-b border-slate-200 font-semibold text-navy">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100">
              {cols.map(c => <td key={c} className="px-2 py-1 text-ink-muted">{String(r[c] ?? '')}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && <div className="text-xs text-ink-muted">No sample rows.</div>}
    </div>
  );
}

const PIPELINE_STAGES = [
  { id: 'geocode',  label: 'Geocoding rows' },
  { id: 'sentinel', label: '11-step QA validation' },
  { id: 'publish',  label: 'Publishing layer' },
  { id: 'export',   label: 'Exporting 5 formats' },
  { id: 'report',   label: 'Building DOCX report' },
  { id: 'deliver',  label: 'Delivering to client workspace' },
  { id: 'janitor',  label: 'Cleanup' },
];

// Pipeline v2 only: shown when the Intake agent detected that the uploaded
// file is missing required or recommended columns for the active project
// type. Soft-warn behavior: the pipeline still ran, but the report skipped
// sections that depend on the missing fields.
function IntakeWarningPanel({ warnings }) {
  const reqs = warnings?.missing_required || [];
  const recs = warnings?.missing_recommended || [];
  if (reqs.length === 0 && recs.length === 0) return null;
  return (
    <div className="card card-pad" style={{ background: '#FEF3C7', borderColor: '#FDE68A', borderWidth: 1 }}>
      <div className="flex items-center gap-2 mb-2">
        <span style={{ fontSize: 18 }}>⚠️</span>
        <div className="text-xs uppercase tracking-widest font-bold" style={{ color: '#92400E' }}>
          Soft warning: missing columns
        </div>
      </div>
      {warnings?.soft_warn_message && (
        <p className="text-sm mb-3" style={{ color: '#78350F' }}>
          {warnings.soft_warn_message}
        </p>
      )}
      {reqs.length > 0 && (
        <div className="mb-3">
          <div className="text-xs font-semibold mb-1" style={{ color: '#7F1D1D' }}>
            Required ({reqs.length}): report skipped sections that needed these
          </div>
          <ul className="text-sm space-y-1" style={{ color: '#78350F' }}>
            {reqs.map(m => (
              <li key={m.key}>
                <strong>{m.key}</strong>: {m.purpose}
                {' '}
                <span className="text-xs italic">(try a column named: {(m.synonyms || []).join(', ')})</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {recs.length > 0 && (
        <div>
          <div className="text-xs font-semibold mb-1" style={{ color: '#92400E' }}>
            Recommended ({recs.length}): report quality is lower without these
          </div>
          <ul className="text-sm space-y-1" style={{ color: '#78350F' }}>
            {recs.map(m => (
              <li key={m.key}>
                <strong>{m.key}</strong>: {m.purpose}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="mt-3 text-xs italic" style={{ color: '#92400E' }}>
        Tip: re-upload the file with these columns added to get a fuller report on the next run.
      </div>
    </div>
  );
}

function PipelineProgress({ running, progress, result }) {
  return (
    <div className="space-y-2">
      {PIPELINE_STAGES.map(st => {
        const p = progress.find(x => x.stage === st.id);
        const status = p?.status || (running ? 'pending' : 'queued');
        return (
          <div key={st.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
            <StageIcon status={status} />
            <div className="flex-1">
              <div className="text-sm font-semibold text-navy">{st.label}</div>
              {p?.detail && <div className="text-xs text-ink-muted">{p.detail}</div>}
            </div>
            {p?.duration_ms && <div className="text-xs font-mono text-ink-muted">{p.duration_ms}ms</div>}
          </div>
        );
      })}
    </div>
  );
}

function StageIcon({ status }) {
  if (status === 'completed') return <div className="w-6 h-6 rounded-full bg-emerald-500 text-white text-xs flex items-center justify-center">✓</div>;
  if (status === 'running') return <div className="w-6 h-6 rounded-full bg-amber-400 text-white text-xs flex items-center justify-center animate-pulse">⏳</div>;
  if (status === 'failed') return <div className="w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">!</div>;
  return <div className="w-6 h-6 rounded-full bg-slate-300 text-white text-xs flex items-center justify-center">·</div>;
}

function FailureCard({ failure, onRetry, onBack }) {
  const failing = failure.failing_rules || [];
  const warning = failure.warning_rules || [];
  return (
    <div className="bg-red-50 border-2 border-red-300 rounded-lg p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="text-3xl">⚠️</div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-red-700 font-bold">
            Pipeline halted at: {failure.stage}
          </div>
          <h3 className="text-xl font-bold text-red-900 mt-1">{failure.user_message}</h3>
        </div>
      </div>

      <div className="bg-white border border-red-200 rounded p-3 space-y-3 text-sm">
        <div>
          <div className="font-semibold text-red-900 text-xs uppercase tracking-widest mb-1">Why this happened</div>
          <div className="text-ink">{failure.why}</div>
        </div>
        <div>
          <div className="font-semibold text-emerald-800 text-xs uppercase tracking-widest mb-1">What to do</div>
          <div className="text-ink">{failure.what_to_do}</div>
        </div>
        {failing.length > 0 && (
          <div>
            <div className="font-semibold text-red-900 text-xs uppercase tracking-widest mb-2">Sentinel rules that FAILed ({failing.length})</div>
            <ul className="space-y-2">
              {failing.map(r => (
                <li key={r.id} className="bg-red-50 border-l-4 border-red-500 pl-3 py-1">
                  <div className="font-mono text-xs text-red-700">{r.id} · {r.label}</div>
                  <div className="text-xs text-ink-muted">{r.detail}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
        {warning.length > 0 && (
          <details>
            <summary className="cursor-pointer text-xs font-semibold text-amber-800 uppercase tracking-widest">
              {warning.length} non-blocking warning(s). Show
            </summary>
            <ul className="mt-2 space-y-1">
              {warning.map(r => (
                <li key={r.id} className="text-xs">
                  <span className="font-mono text-amber-700">{r.id}</span> · {r.label}: <span className="text-ink-muted">{r.detail}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
        {failure.technical_detail && (
          <details>
            <summary className="cursor-pointer text-xs text-ink-muted">Technical detail (for support email)</summary>
            <pre className="bg-slate-900 text-emerald-300 rounded p-2 mt-2 text-xs overflow-x-auto whitespace-pre-wrap break-all">{failure.technical_detail}</pre>
          </details>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        <button className="btn btn-primary" onClick={onRetry}>Retry pipeline</button>
        <button className="btn btn-ghost border border-slate-300" onClick={onBack}>← Back to Step 3 (re-map columns)</button>
        <a className="btn btn-ghost border border-slate-300" href="/api/data-requirements.docx" download>↓ Data Prep Guide</a>
        <a className="btn btn-ghost border border-slate-300" href="mailto:info@atlasgeo.ai?subject=MAPTRIUM job failure">Email support</a>
      </div>
    </div>
  );
}

function DeliveryCard({ delivery, sentinel, onDone }) {
  return (
    <div className="card card-pad bg-emerald-50 border-emerald-200">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Delivered</div>
          <div className="font-bold text-navy text-lg">Files are in the client workspace</div>
        </div>
        {sentinel && <span className={`tag ${sentinel.verdict === 'PASS' ? 'tag-green' : sentinel.verdict === 'WARN' ? 'tag-amber' : 'tag-red'}`}>QA: {sentinel.verdict}</span>}
      </div>
      <ul className="text-sm space-y-1 mb-4">
        {(delivery.files || []).map((f, i) => (
          <li key={i} className="flex gap-2"><span>📎</span><span className="font-mono text-xs">{f.name}</span></li>
        ))}
      </ul>
      <div className="flex gap-2">
        {delivery.folder_url && <a className="btn btn-gold" target="_blank" rel="noreferrer" href={delivery.folder_url}>Open client workspace ↗</a>}
        <button className="btn btn-primary" onClick={onDone}>View job detail →</button>
      </div>
    </div>
  );
}

// ---------- Data Requirements modal ----------
function DataRequirementsModal({ onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.dataRequirements().then(setData).catch(() => {}); }, []);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-8" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white rounded-t-xl">
          <div>
            <div className="tag tag-gold mb-1">Data prep</div>
            <h2 className="text-2xl font-bold text-navy">What your file needs</h2>
          </div>
          <button className="text-2xl text-ink-muted hover:text-ink" onClick={onClose}>×</button>
        </div>
        <div className="p-6 space-y-6 text-sm">
          {!data ? (
            <div className="text-ink-muted">Loading…</div>
          ) : (
            <>
              <section>
                <h3 className="font-bold text-navy text-lg mb-2">The minimum: ONE of these four location formats</h3>
                <p className="text-ink-muted mb-3">MAPTRIUM needs to know where each row goes on the map. Pick whichever format your data already has. You don't need all four.</p>
                <div className="grid md:grid-cols-2 gap-3">
                  {data.location_options.map(opt => (
                    <div key={opt.key} className="border border-slate-200 rounded-lg p-3">
                      <div className="font-semibold text-navy mb-1">{opt.label}</div>
                      <div className="text-xs space-y-1">
                        {opt.required_columns.map((c, i) => (
                          <div key={i} className="font-mono bg-slate-50 px-2 py-1 rounded">
                            <strong>{c.name}</strong>: {c.type} <span className="text-ink-muted">e.g. {c.example}</span>
                          </div>
                        ))}
                      </div>
                      <div className="text-xs mt-2 text-ink-muted"><strong>When:</strong> {opt.when_to_use}</div>
                      <div className="text-xs text-emerald-700"><strong>Pros:</strong> {opt.pros}</div>
                      <div className="text-xs text-red-700"><strong>Cons:</strong> {opt.cons}</div>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h3 className="font-bold text-navy text-lg mb-2">Strongly recommended (unlocks the rest of the report)</h3>
                <ul className="list-disc pl-5 space-y-1">
                  {data.recommended_columns.map((r, i) => (
                    <li key={i}><strong>{r.name}</strong>: {r.purpose}</li>
                  ))}
                </ul>
              </section>

              <section>
                <h3 className="font-bold text-navy text-lg mb-2">Hard caps</h3>
                <div className="bg-slate-50 rounded p-3 text-xs">
                  <div><strong>Maximum rows:</strong> {data.caps.max_rows.toLocaleString()}</div>
                  <div><strong>Maximum file size:</strong> {data.caps.max_file_mb} MB</div>
                  <div><strong>Supported formats:</strong> {data.caps.supported_formats.join(', ')}</div>
                  <div><strong>Encoding:</strong> {data.caps.encoding}</div>
                </div>
              </section>

              <section>
                <h3 className="font-bold text-navy text-lg mb-2">Common mistakes to avoid</h3>
                <ul className="list-disc pl-5 space-y-1 text-red-700">
                  {data.donts.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              </section>

              <section>
                <h3 className="font-bold text-navy text-lg mb-2">The bare minimum example</h3>
                <p className="text-ink-muted mb-2">{data.minimum_example.description}</p>
                <pre className="bg-slate-900 text-emerald-300 rounded p-3 text-xs overflow-x-auto">{data.minimum_example.sample_csv}</pre>
                <p className="text-xs text-ink-muted italic mt-2">{data.minimum_example.explanation}</p>
              </section>
            </>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-between items-center bg-slate-50 rounded-b-xl sticky bottom-0">
          <a href="/api/data-requirements.docx" download className="text-sm text-navy font-semibold underline">↓ Download as Word document</a>
          <button className="btn btn-primary" onClick={onClose}>Got it</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Project brief & context (inline in mapping step) ----------
function ProjectBriefPanel({ brief, onChange, noWrapper }) {
  const b = brief || {
    description: '',
    event_date: '',
    hours: '',
    expected_attendance: '',
    parking_info: '',
    contact_info: '',
    special_notes: '',
    custom_fields: [],
  };
  const update = (patch) => onChange({ ...b, ...patch });
  const setCustom = (i, key, val) => {
    const next = [...(b.custom_fields || [])];
    next[i] = { ...next[i], [key]: val };
    update({ custom_fields: next });
  };
  const addCustom = () => update({ custom_fields: [...(b.custom_fields || []), { label: '', value: '' }] });
  const removeCustom = (i) => update({ custom_fields: (b.custom_fields || []).filter((_, idx) => idx !== i) });

  const inner = (
    <div className={noWrapper ? "space-y-4" : "pt-4 border-t border-slate-200 space-y-4"}>
      {!noWrapper && (
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-navy">Project brief & context</h3>
          <InfoBadge
            title="Why this matters"
            body={'Anything you type here flows into the DOCX report. The agent uses it to write a richer narrative (operating hours, expected crowds, parking, what the event is, why this map matters). Treat it like the cover memo you’d write for a reviewer. Optional, but reports with context score noticeably better.'}
          />
          <span className="ml-2 text-xs text-ink-muted italic">All fields optional. Flows into report.</span>
        </div>
      )}

      <div>
        <label className="label">Description / context (what is this map for?)</label>
        <textarea
          className="input min-h-[140px] resize-y"
          placeholder={
            'Example: "Tacos & Trucks Saturday Fest at Memorial Park. 12 food trucks plus a kids zone and bandstand. ' +
            'Expecting 2,500–3,500 attendees across the day. Operating 11am–9pm. Parking on Elm St lot (350 spaces) ' +
            'plus overflow at the high school. The map shows truck pads, restroom locations, water tie-ins, ' +
            'and emergency access lanes."'
          }
          value={b.description || ''}
          onChange={(e) => update({ description: e.target.value })}
        />
        <div className="text-xs text-ink-muted mt-1">
          Two or three sentences is plenty. Whatever you write here appears in a "Project Context" section near the top of the DOCX.
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="label">Event / project date(s)</label>
          <input
            className="input"
            placeholder="e.g. Saturday, June 21 2026 or June 21–22 2026"
            value={b.event_date || ''}
            onChange={(e) => update({ event_date: e.target.value })}
          />
        </div>
        <div>
          <label className="label">Operating hours</label>
          <input
            className="input"
            placeholder="e.g. 11:00 AM – 9:00 PM"
            value={b.hours || ''}
            onChange={(e) => update({ hours: e.target.value })}
          />
        </div>
        <div>
          <label className="label">Expected attendance / crowd size</label>
          <input
            className="input"
            placeholder="e.g. 2,500–3,500 across the day"
            value={b.expected_attendance || ''}
            onChange={(e) => update({ expected_attendance: e.target.value })}
          />
        </div>
        <div>
          <label className="label">Parking / access</label>
          <input
            className="input"
            placeholder="e.g. 350-space lot on Elm St + overflow at HS"
            value={b.parking_info || ''}
            onChange={(e) => update({ parking_info: e.target.value })}
          />
        </div>
        <div className="md:col-span-2">
          <label className="label">On-site contact / point of contact</label>
          <input
            className="input"
            placeholder="e.g. Event Ops: Mia Sandoval, 555-0144, mia@town.gov"
            value={b.contact_info || ''}
            onChange={(e) => update({ contact_info: e.target.value })}
          />
        </div>
        <div className="md:col-span-2">
          <label className="label">Special notes / hazards / accessibility</label>
          <textarea
            className="input min-h-[80px] resize-y"
            placeholder="e.g. Bandstand on the green; generator near pad 4; ADA path along the gravel walk; lightning shelter at pavilion B."
            value={b.special_notes || ''}
            onChange={(e) => update({ special_notes: e.target.value })}
          />
        </div>
      </div>

      <details className="bg-slate-50 border border-slate-200 rounded p-3">
        <summary className="cursor-pointer text-sm font-semibold text-navy">
          Add custom fields (vendor count, permit #, weather plan, anything else)
        </summary>
        <div className="mt-3 space-y-2">
          {(b.custom_fields || []).map((cf, i) => (
            <div key={i} className="flex gap-2">
              <input
                className="input flex-1"
                placeholder="Label (e.g. Permit #)"
                value={cf.label || ''}
                onChange={(e) => setCustom(i, 'label', e.target.value)}
              />
              <input
                className="input flex-1"
                placeholder="Value (e.g. PW-2026-0341)"
                value={cf.value || ''}
                onChange={(e) => setCustom(i, 'value', e.target.value)}
              />
              <button
                type="button"
                className="text-xs text-red-700 underline"
                onClick={() => removeCustom(i)}
              >Remove</button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={addCustom}
          >+ Add field</button>
        </div>
      </details>
    </div>
  );
  return inner;
}

// ---------- Symbology picker (inline in mapping step) ----------
function SymbologyPicker({ columns, symbology, onChange, noWrapper }) {
  const [catalog, setCatalog] = useState(null);
  const [uniqueValues, setUniqueValues] = useState([]);
  const [iconSearch, setIconSearch] = useState('');
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const s = symbology || {
    palette: 'atlas_brand', shape: 'circle', icon: null, icon_color: '#FFFFFF', size: 14,
    stroke_color: '#FFFFFF', stroke_width: 2, fill_alpha: 1,
    color_by: null, categories: {}, size_by: null, size_min: 6, size_max: 22,
  };

  useEffect(() => {
    api.symbologyCatalog().then(setCatalog).catch(() => setCatalog(null));
  }, []);

  const update = (patch) => onChange({ ...s, ...patch });
  const updateCategory = (val, color) => onChange({ ...s, categories: { ...(s.categories || {}), [val]: color } });

  const palette = catalog?.palettes?.find(p => p.key === s.palette) || catalog?.palettes?.[0];

  return (
    <div className={noWrapper ? "space-y-4" : "pt-4 border-t border-slate-200 space-y-4"}>
      {!noWrapper && (
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-navy">Map symbology</h3>
          <InfoBadge title="Custom symbology" body={'Pick how the points look on the map. Choose a palette for color, a shape, and (optionally) a column to drive color or size (e.g. color by severity, size by total crashes). Every choice flows into the static PNG in the DOCX and the colors in the legend.'} />
          <span className="ml-2 tag tag-gold">Enterprise feature</span>
        </div>
      )}

      <div>
        <label className="label flex items-center gap-2">
          Icon (optional)
          <InfoBadge title="Public-safety + amenity icons" body={'Pick a recognizable glyph drawn on top of the marker badge (crosswalk, ADA, restroom, picnic table, tent, fire hydrant, police, school zone, etc). Use this when the data IS the feature (e.g. an inventory of crosswalks) instead of a statistic.'} />
        </label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn btn-ghost border border-slate-300"
            onClick={() => setIconPickerOpen(o => !o)}
          >
            {s.icon
              ? <span><strong>{(catalog?.icon_groups || []).flatMap(g => g.icons).find(i => i.key === s.icon)?.label || s.icon}</strong> (change)</span>
              : 'Pick an icon (none)'}
          </button>
          {s.icon && (
            <button type="button" className="text-xs text-red-700 underline" onClick={() => update({ icon: null })}>
              Clear icon
            </button>
          )}
          <span className="ml-auto flex items-center gap-2 text-xs">
            <span>Icon color (inside the dot)</span>
            <input type="color" value={s.icon_color || '#FFFFFF'} onChange={e => update({ icon_color: e.target.value })} />
          </span>
        </div>

        {iconPickerOpen && (
          <div className="mt-3 border border-slate-300 rounded p-3 bg-white max-h-96 overflow-y-auto">
            <input
              className="input mb-3"
              placeholder="Search icons (crosswalk, ada, picnic, hydrant…)"
              value={iconSearch}
              onChange={e => setIconSearch(e.target.value)}
              autoFocus
            />
            {(catalog?.icon_groups || []).map(group => {
              const q = iconSearch.trim().toLowerCase();
              const matches = group.icons.filter(i =>
                !q || i.label.toLowerCase().includes(q) || i.key.toLowerCase().includes(q)
              );
              if (matches.length === 0) return null;
              return (
                <div key={group.group} className="mb-4">
                  <div className="text-xs uppercase tracking-widest text-ink-muted mb-2">{group.group}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {matches.map(i => (
                      <button
                        key={i.key}
                        type="button"
                        onClick={() => { update({ icon: i.key }); setIconPickerOpen(false); }}
                        className={`text-left px-2 py-1.5 text-sm rounded border ${s.icon === i.key ? 'border-navy bg-navy/10 font-semibold' : 'border-slate-200 hover:border-navy'}`}
                      >
                        {i.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {/* Palette */}
        <div>
          <label className="label">Color set</label>
          <select className="input" value={s.palette} onChange={e => update({ palette: e.target.value })}>
            {(catalog?.palettes || []).map(p => (
              <option key={p.key} value={p.key}>{p.name}</option>
            ))}
          </select>
          {palette && (
            <div className="flex gap-1 mt-2">
              {palette.colors.map((c, i) => (
                <div key={i} className="w-6 h-6 rounded border border-slate-300" style={{ backgroundColor: c }} title={c} />
              ))}
            </div>
          )}
        </div>

        {/* Shape */}
        <div>
          <label className="label">Dot shape</label>
          <select className="input" value={s.shape} onChange={e => update({ shape: e.target.value })}>
            {(catalog?.shapes || ['circle','square','triangle','diamond','star','cross','pin']).map(sh => (
              <option key={sh} value={sh}>{sh.charAt(0).toUpperCase() + sh.slice(1)}</option>
            ))}
          </select>
        </div>

        {/* Size */}
        <div>
          <label className="label">Dot size: {s.size}</label>
          <input
            type="range" min={4} max={30} step={1} value={s.size}
            onChange={e => update({ size: Number(e.target.value) })}
            className="w-full"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="label">Color each dot based on… (optional)</label>
          <select
            className="input"
            value={s.color_by || ''}
            onChange={e => update({ color_by: e.target.value || null })}
          >
            <option value="">Single color (use palette[0])</option>
            {(columns || []).map(c => <option key={c.name || c} value={c.name || c}>{c.name || c}</option>)}
          </select>
          <div className="text-xs text-ink-muted mt-1">
            Each distinct value gets a color from the palette. Override individual values below.
          </div>
          <div className="mt-3">
            <label className="label">Legend title (optional)</label>
            <input
              type="text"
              className="input"
              value={s.legend_title ?? ''}
              placeholder={s.color_by ? `Default: ${s.color_by.replace(/[_-]+/g,' ').replace(/\b\w/g, c => c.toUpperCase())}` : 'e.g. KABCO Severity'}
              onChange={e => update({ legend_title: e.target.value })}
            />
            <div className="text-xs text-ink-muted mt-1">
              Shown above the color swatches in the map's legend box. Leave blank to use a humanized version of the color-by field name. Type a single space to suppress the title row entirely.
            </div>
          </div>
        </div>
        <div>
          <label className="label">Make dots bigger when this number is bigger… (optional)</label>
          <select
            className="input"
            value={s.size_by || ''}
            onChange={e => update({ size_by: e.target.value || null })}
          >
            <option value="">Same size for every point</option>
            {(columns || []).map(c => <option key={c.name || c} value={c.name || c}>{c.name || c}</option>)}
          </select>
          <div className="text-xs text-ink-muted mt-1">
            Points scale from {s.size_min}px (min value) to {s.size_max}px (max value).
          </div>
        </div>
      </div>

      {/* Per-value overrides if color_by is set */}
      {s.color_by && (
        <details className="border border-slate-200 rounded p-3 bg-slate-50">
          <summary className="cursor-pointer text-sm font-semibold text-navy">
            Override colors for specific values of "{s.color_by}"
          </summary>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {Object.keys(s.categories || {}).length === 0 && (
              <div className="text-ink-muted col-span-2">
                After your first job runs, distinct values appear here. To add an override now, type the value and pick a color:
              </div>
            )}
            {Object.entries(s.categories || {}).map(([val, color]) => (
              <div key={val} className="flex items-center gap-2">
                <div className="flex-1 text-sm font-mono truncate" title={val}>{val}</div>
                <input
                  type="color" value={color}
                  onChange={e => updateCategory(val, e.target.value)}
                />
                <button
                  type="button"
                  className="text-xs text-red-700 underline"
                  onClick={() => {
                    const next = { ...(s.categories || {}) };
                    delete next[val];
                    update({ categories: next });
                  }}
                >Remove</button>
              </div>
            ))}
            <CategoryAdder onAdd={(val, color) => updateCategory(val, color)} />
          </div>
        </details>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className="label">Outline color around each dot</label>
          <input
            type="color" value={s.stroke_color || '#FFFFFF'}
            onChange={e => update({ stroke_color: e.target.value })}
            className="h-10 w-full border rounded"
          />
        </div>
        <div>
          <label className="label">Outline thickness: {s.stroke_width}</label>
          <input
            type="range" min={0} max={6} step={1} value={s.stroke_width}
            onChange={e => update({ stroke_width: Number(e.target.value) })}
            className="w-full"
          />
        </div>
        <div>
          <label className="label">How see-through the dots are: {Math.round((s.fill_alpha || 1) * 100)}%</label>
          <input
            type="range" min={20} max={100} step={5}
            value={Math.round((s.fill_alpha || 1) * 100)}
            onChange={e => update({ fill_alpha: Number(e.target.value) / 100 })}
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
}

function CategoryAdder({ onAdd }) {
  const [val, setVal] = useState('');
  const [color, setColor] = useState('#dc2626');
  return (
    <div className="flex items-center gap-2 col-span-2 pt-2 border-t border-slate-200">
      <input
        className="input flex-1 text-sm"
        placeholder='Value (e.g. "Fatal" or "K")'
        value={val} onChange={e => setVal(e.target.value)}
      />
      <input type="color" value={color} onChange={e => setColor(e.target.value)} />
      <button
        type="button"
        className="btn btn-sm btn-ghost"
        disabled={!val.trim()}
        onClick={() => { if (val.trim()) { onAdd(val.trim(), color); setVal(''); } }}
      >Add</button>
    </div>
  );
}


function PptxPriceChip({ planGroup }) {
  const included = ["professional", "business", "enterprise"].includes((planGroup || "").toLowerCase());
  if (included) {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 font-semibold">Included</span>;
  }
  return <span className="text-xs px-2 py-0.5 rounded-full bg-gold/20 text-gold-700 border border-gold/40 font-semibold">+ $150</span>;
}
